import { useSelector, useDispatch } from 'react-redux'
import { setUser, initialState, clearUser  } from 'store/auth/userSlice'
import { resetState } from 'store/base/commonSlice'
import { apiSignIn, apiSignOut, apiSignUp } from 'services/AuthService'
import { onSignInSuccess, onSignOutSuccess } from 'store/auth/sessionSlice'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import { getConfig } from 'configs/edition.config'

import { useMemo } from 'react'
import { googleLogout, useGoogleLogin } from '@react-oauth/google'
import { persistor } from 'store/index'; // persistor 임포트 추가
import BaseService from 'services/BaseService' // QPM 추적을 위한 BaseService 추가
import { purgeUserScopedData, purgeIfUserChanged as purgeIfUserChangedUtil } from 'utils/authPurge';

function useAuth() {
    const dispatch = useDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    // Standalone 모드 여부 확인
    const editionConfig = useMemo(() => getConfig(), [])

    const { token, signedIn } = useSelector((state) => state.auth.session)
    const currentUserId = useSelector((state) => state.auth.user?.userId || '')

    /**
     * signIn 진입 직전에 호출. 새 사용자가 기존 사용자와 다르면 stale 데이터를 모두 청소.
     * (sign-out 누락 후 다른 계정으로 로그인 케이스 보호)
     */
    const purgeIfUserChanged = (incomingUserId) =>
        purgeIfUserChangedUtil(dispatch, currentUserId, incomingUserId);

    const signIn = async (values) => {
        try {
            const resp = await apiSignIn(values)
            if (resp.data) {
                const { token } = resp.data
                await purgeIfUserChanged(resp.data.user?.userId);
                dispatch(onSignInSuccess(token))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || {
                                avatar: '',
                                userName: 'Anonymous',
                                authority: ['USER'],
                                email: '',
                            }
                        )
                    )
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const signInWithGoogle = async (credential) => {
        try {
            // BaseService를 사용하여 QPM 추적 활성화
            const response = await BaseService.post('/google-login', { credential })
            const data = response.data
            if (data.success) {
                const { token, user } = data
                await purgeIfUserChanged(user?.userId);
                dispatch(onSignInSuccess(token))
                dispatch(setUser(user))
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath)
                return user
            } else {
                throw new Error('Google sign-in failed')
            }
        } catch (error) {
            console.error('Google sign-in failed', error)
            throw error
        }
    }

    const signInWithNaver = async (naverUser) => {
        try {
            // 네이버 사용자 정보를 가져오는 API 호출 및 사용자 정보 설정
            await purgeIfUserChanged(naverUser?.userId);
            dispatch(setUser({ ...naverUser }))
            //navigate('/dashboard')
            navigate('/')
        } catch (error) {
            console.error('Naver sign-in failed', error)
            return { status: 'failed', message: 'Naver sign-in failed' }
        }
    }    

    const signUp = async (values) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data) {
                const { token } = resp.data
                await purgeIfUserChanged(resp.data.user?.userId);
                dispatch(onSignInSuccess(token))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || {
                                avatar: '',
                                userName: 'Anonymous',
                                authority: ['USER'],
                                email: '',
                            }
                        )
                    )
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(onSignOutSuccess())
        dispatch(setUser(initialState))
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    /*
    const signOut = async () => {
        await apiSignOut()
        handleSignOut()
    }
    */
    const signOut = async () => {
        try {
            await apiSignOut();
            handleSignOut();
            googleLogout();
            dispatch(setUser(initialState));
            // user-scoped 저장소 일괄 정리 (Redux base.common + redux-persist + 레거시 base + historyDB)
            await purgeUserScopedData(dispatch);
            navigate('/sign-in');
        } catch (error) {
            console.error('Sign out error:', error);
            // 에러가 발생해도 로그아웃 처리는 계속 진행
            handleSignOut();
            dispatch(setUser(initialState));
            await purgeUserScopedData(dispatch);
            navigate('/sign-in');
        }
    }

    return {
        authenticated: editionConfig.isStandalone || signedIn,  // Standalone 모드는 항상 인증됨
        signIn,
        signInWithGoogle,
        signInWithNaver,
        signUp,
        signOut,
    }
}

export default useAuth
